import CompAddressUtils from '@/models/comp/CompAddressUtils';
import ICompAddresses from '@/models/comp/ICompAddresses';
import ICompAddress from '@/models/CompAddress';
import ISalesHistory from '@/models/sales-history/ISalesHistory';
import IPriorSalesAnalyses from '@/models/sales-history/IPriorSalesAnalyses';
import IPriorSalesAnalysis from '@/models/sales-history/IPriorSalesAnalysis';
import ISalesHistoryProperty, { newSalesHistoryProperty } from '@/models/sales-history/ISalesHistoryProperty';
import ISalesHistorySelections, { newSalesHistorySelections } from '@/models/sales-history/ISalesHistorySelections';
import HostExchangeService from '@/services/2.6/HostExchangeService';
import SalesHistoryService from '@/services/2.6/SalesHistoryService';
import TheWebEnvConfigService from '@/services/TheWebEnvConfigService';
import { salesHistoryStore } from '@/store';
import DateTimeUtils from '@/utils/DateTimeUtils';
import ProductFeaturesUtils from '@/utils/ProductFeaturesUtils';
import IExportSalesHistory from '@/models/sales-history/IExportSalesHistory';
import IExportSalesHistoryProperties from '@/models/sales-history/IExportSalesHistoryProperties';
import IExportSalesHistoryProperty from '@/models/sales-history/IExportSalesHistoryProperty';
import IExportSaleData from '@/models/sales-history/IExportSaleData';
import ISaleData from '@/models/sales-history/ISaleData';
import utils from '@/utils/QSUtils';
import ICompFinalData from '@/models/comp-final/ICompFinalData';

const ordinals = ['first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth', 'tenth'];

export default {
    /**
     * Get a sales history property from the sales history store. If one doesn't exist for
     * the given comp address will fetch from the sales history service, put the result into
     * the store and return the reactive property from the store.
     * @param compAddress
     * @returns a sales history property with an array of sales history which may be empty
     */
    async getByCompAddress(compAddress: ICompAddress): Promise<ISalesHistoryProperty> {
        const address1UnitNoZipCode = CompAddressUtils.getAddress1UnitNoAndZipCode(compAddress);

        let salesHistoryProperty = salesHistoryStore.SalesHistoryByAddress1UnitNoZipCode(address1UnitNoZipCode);
        if (salesHistoryProperty) {
            // Found in the store
            return salesHistoryProperty;
        }

        // Fetch from the service and place result into the store
        await fetchAndStore(compAddress);

        // Now that it's in the store
        salesHistoryProperty = salesHistoryStore.SalesHistoryByAddress1UnitNoZipCode(address1UnitNoZipCode);
        if (!salesHistoryProperty) {
            // Should never occur but we don't want to include null as a return value.
            salesHistoryProperty = newSalesHistoryProperty(address1UnitNoZipCode);
        }
        return salesHistoryProperty;
    },

    /**
     * Fetch any sales history selections from a prior session and put them in the store.
     */
    async fetchSelectionsAndStore() {
        const hostExchangeService = HostExchangeService.getInstance();
        const salesHistorySelectionsArray = await hostExchangeService.getSalesHistorySelectionsFromReport();
        if (salesHistorySelectionsArray) {
            for (const salesHistorySelections of salesHistorySelectionsArray) {
                await salesHistoryStore.addOrReplaceSelections(salesHistorySelections);
            }
        }
        //console.log(`SalesHistoryManager.fetchAndStore(), Done`);
    },

    /**
     * Get a sales history selections from the sale history store by the given comp address.
     * Will add one to the store if needed.
     * @param compAddress
     * @returns
     */
    async getSelectionsByCompAddress(compAddress: ICompAddress): Promise<ISalesHistorySelections> {
        const address1UnitNoZipCode = CompAddressUtils.getAddress1UnitNoAndZipCode(compAddress);

        let salesHistorySelections = salesHistoryStore.SelectionsByAddress1UnitNoZipCode(address1UnitNoZipCode);
        if (!salesHistorySelections) {
            // Not found in the store. Create a new one and add it to the store.
            salesHistorySelections = newSalesHistorySelections(address1UnitNoZipCode);
            await salesHistoryStore.addOrReplaceSelections(salesHistorySelections);
        }

        return salesHistorySelections;
    },

    /**
     * Save the given selections to the store.
     * @param selections
     * @returns true on added, false on replaced
     */
    async saveSelections(selections: ISalesHistorySelections): Promise<boolean> {
        const added = await salesHistoryStore.addOrReplaceSelections(selections);
        return added;
    },

    /**
     * Remove a selections from the store using the given comp address.
     * @param compAddress
     * @returns true on removed, false on not found
     */
    async removeSelections(compAddress: ICompAddress): Promise<boolean> {
        const removed = await salesHistoryStore.removeSelections(compAddress);
        return removed;
    },

    /**
     * Generate the analyses array of prior sales analysis objects for the given comp addresses.
     * Uses the sales history selections in the sales history store.
     * The resulting analysis object zero-based comp numbers align with the order of comp addresses.
     * Requires Smart Data be enabled (Product Features) for the user.
     * @param compAddresses
     * @returns
     */
    async generateAnalyses(compFinalData: ICompFinalData): Promise<IPriorSalesAnalyses> {
        const analyses: IPriorSalesAnalyses = [];

        if (ProductFeaturesUtils.isSmartDataEnabled()) {
            let compNumber = 0;
            for (const compFinalProperty of compFinalData) {
                const selections = await this.getSelectionsByCompAddress(compFinalProperty.address);
                if (selections.includeAnalysis && selections.chronologyNumbers.length > 0) {
                    // console.log(`SalesHistoryManager.generateAnalyses(), Generating for [${CompAddressUtils.getShortDescription(compFinalProperty.address)}]`);

                    const salesHistoryProperty = await this.getByCompAddress(compFinalProperty.address);
                    if (salesHistoryProperty.salesHistory.length > 0) {
                        const analysisText = this.generateAnalysisText(compFinalProperty.address, salesHistoryProperty.salesHistory, selections.chronologyNumbers);

                        const analysis: IPriorSalesAnalysis = {
                            address1AndUnitNo: CompAddressUtils.combineAddress1AndUnitNo(compFinalProperty.address),
                            compNumber: compNumber,
                            compKey: compFinalProperty.compKey ?? null,
                            analysis: analysisText,
                        };

                        analyses.push(analysis);
                    }
                }

                compNumber++;
            }
        }

        return analyses;
    },

    /**
     * Generate the analysis of prior sales for the given comp address, sales history and ordered chronology numbers.
     * @param compAddress
     * @param salesHistory
     * @param chronologyNumbers
     * @returns
     */
    generateAnalysisText(compAddress: ICompAddress, salesHistory: ISalesHistory, chronologyNumbers: string[]): string {
        let analysis = '';
        for (const { index, chronologyNumber } of chronologyNumbers.map((chronologyNumber, index) => ({ index, chronologyNumber }))) {
            const saleDataIdx = salesHistory.findIndex((sd) => sd.chronologyNumber === chronologyNumber);
            if (saleDataIdx >= 0) {
                const saleData = salesHistory[saleDataIdx];
                const address = CompAddressUtils.getShortDescription(compAddress);
                const saleDate = DateTimeUtils.fromFormat(saleData.saleDate ?? '', 'YYYY-MM-DD');
                const formattedSaleDate = DateTimeUtils.toFormat(saleDate, 'MM/DD/YYYY', 'Unknown');
                const recordedDate = DateTimeUtils.fromFormat(saleData.recordedDate ?? '', 'YYYY-MM-DD');
                const formattedRecordedDate = DateTimeUtils.toFormat(recordedDate, 'MM/DD/YYYY', 'Unknown');

                if (index === 0) {
                    analysis += `CoreLogic public records indicate the most recent transfer for ${address} occurred on ${formattedSaleDate}`;
                    analysis += ` (${saleData.documentType ?? 'Unknown'} - Doc #${saleData.documentNumber ?? 'Unknown'} recorded on ${formattedRecordedDate}). `;
                } else if (index < ordinals.length) {
                    analysis += `The ${ordinals[index]} most recent transfer occurred on ${formattedSaleDate}`;
                    analysis += ` (${saleData.documentType ?? 'Unknown'} - Doc #${saleData.documentNumber ?? 'Unknown'} recorded on ${formattedRecordedDate}). `;
                } else {
                    // Reached the limit on our ordinals, stop adding any more.
                    break;
                }
            } else {
                // Didn't find sale data for one of the chronology numbers!
                console.log(`SalesHistoryManager.generateAnalysis(), Didn't find sale data for chronology number[${chronologyNumber}]!`);
            }
        }
        return analysis.trim();
    },

    async getForAddenda(compFinalData: ICompFinalData): Promise<IExportSalesHistoryProperties> {
        const addendaProperties: IExportSalesHistoryProperties = [];

        if (ProductFeaturesUtils.isSmartDataEnabled()) {
            let compNumber = 0;
            for (const compFinalProperty of compFinalData) {
                const selections = await this.getSelectionsByCompAddress(compFinalProperty.address);
                if (selections.includeInAddenda) {
                    // console.log(`SalesHistoryManager.getForAddenda(), Getting for [${CompAddressUtils.getShortDescription(compFinalProperty.address)}]`);

                    const salesHistoryProperty = await this.getByCompAddress(compFinalProperty.address);
                    if (salesHistoryProperty.salesHistory.length > 0) {
                        const exportHistory: IExportSalesHistory = [];
                        for (const saleData of salesHistoryProperty.salesHistory) {
                            const exportSaleData = convertSaleDataForExport(saleData);
                            exportHistory.push(exportSaleData);
                        }
                        const exportProperty: IExportSalesHistoryProperty = {
                            address1AndUnitNo: CompAddressUtils.combineAddress1AndUnitNo(compFinalProperty.address),
                            compNumber: compNumber,
                            compKey: compFinalProperty.compKey ?? null,
                            salesHistory: exportHistory,
                        };

                        addendaProperties.push(exportProperty);
                    }
                }

                compNumber++;
            }
        }

        return addendaProperties;
    },
};

/**
 * Fetches sales history for the given comp address and places the results in the sales history store.
 * A sales history property is created containing the sales history along with an Address1UnitNoZipCode.
 * The sales history may be empty on no history obtained.
 * @see getByCompAddress in this manager for getting the sales history property from the store
 * @param compAddress
 */
async function fetchAndStore(compAddress: ICompAddress) {
    const webEnvConfig = await TheWebEnvConfigService.getInstance().getConfigByHost();
    const service = new SalesHistoryService(webEnvConfig);

    let salesHistory = await service.get(compAddress);
    if (salesHistory) {
        handleDerivedFields(salesHistory);
    } else {
        salesHistory = [];
    }

    const address1UnitNoZipCode = CompAddressUtils.getAddress1UnitNoAndZipCode(compAddress);
    const salesHistoryProperty = newSalesHistoryProperty(address1UnitNoZipCode);
    salesHistoryProperty.salesHistory = salesHistory;

    const added = await salesHistoryStore.addOrReplaceProperty(salesHistoryProperty);

    const addressDescription = CompAddressUtils.getDescription(compAddress);
    //console.log(`SalesHistoryManager.fetchAndStore(), ${added ? 'Added' : 'Replaced'}, Address[${addressDescription}], Count[${salesHistoryProperty.salesHistory.length}]`);
}

function handleDerivedFields(salesHistory: ISalesHistory) {
    for (const saleData of salesHistory) {
        if (saleData.primaryCategoryCode) {
            saleData.armsLength = saleData.primaryCategoryCode.toLowerCase() === 'a';
        }
    }
}

function convertSaleDataForExport(saleData: ISaleData): IExportSaleData {
    const price = utils.formatNumberToCommas(saleData.price);

    const exportSaleData: IExportSaleData = {
        chronologyNumber: saleData.chronologyNumber ?? '',
        saleDate: formatDate(saleData.saleDate),
        recordedDate: formatDate(saleData.recordedDate),
        price: price ?? '',
        buyer: saleData.grantee ?? '',
        seller: saleData.grantor ?? '',
        documentType: saleData.documentType ?? '',
        documentNumber: saleData.documentNumber ?? '',
        armsLength: saleData.armsLength ? 'Yes' : 'No',
    };

    return exportSaleData;
}

/**
 * Format the sale date or recorded date as MM/DD/YYYY or 'N/A'
 */
function formatDate(dateString: string | null) {
    if (dateString) {
        const dateTime = DateTimeUtils.fromFormat(dateString, 'YYYY-MM-DD');
        const formatted = DateTimeUtils.toFormat(dateTime, 'MM/DD/YYYY', 'N/A');
        return formatted;
    }
    return 'N/A';
}
